import type {
  IExtraCondition,
  IExtraConditionEq,
  IExtraConditionGroup,
  IExtraConditionIn,
  IExtraConditionLike,
} from '../../types';

import { EGroupOperator, EComparisonOperator } from '../../types';

const DEFAULT_GROUP_OPERATOR = EGroupOperator.OR;

const joinFieldClauses = (
  parts: string[],
  groupOperator: EGroupOperator = DEFAULT_GROUP_OPERATOR,
) => {
  if (!parts.length) return '';
  if (parts.length === 1) return parts[0];
  return `(${parts.join(` ${groupOperator} `)})`;
};

const buildEq = ({ fields, value, groupOperator }: IExtraConditionEq) => {
  if (!fields.length) return '';
  return joinFieldClauses(
    fields.map((field) => `${field} = ${value}`),
    groupOperator,
  );
};

const buildIn = ({ fields, values, groupOperator }: IExtraConditionIn) => {
  if (!fields.length || !values.length) return '';
  return joinFieldClauses(
    fields.map((field) => `${field} IN (${values.join(', ')})`),
    groupOperator,
  );
};

// GemXQL's WHERE tokenizer has no escape syntax: it just toggles quote-mode on the next
// occurrence of whichever quote char opened the atom, so an embedded `'` in the value ends
// the literal early. The value is bound as a query parameter server-side (no SQL injection
// risk), so the only thing to get right here is picking a delimiter the value doesn't contain.
const buildLike = ({ fields, value, groupOperator }: IExtraConditionLike) => {
  const trimmed = value.trim();
  if (!trimmed || !fields.length) return '';
  const quote = trimmed.includes("'") && !trimmed.includes('"') ? '"' : "'";
  return joinFieldClauses(
    fields.map((field) => `${field} LIKE ${quote}%${trimmed}%${quote}`),
    groupOperator,
  );
};

const buildGroup = ({ conditions, groupOperator }: IExtraConditionGroup) => {
  const parts = conditions.map(buildExtraCondition).filter(Boolean);
  return joinFieldClauses(parts, groupOperator);
};

export const buildExtraCondition = (condition: IExtraCondition): string => {
  switch (condition.operator) {
    case EComparisonOperator.EQ:
      return buildEq(condition);
    case EComparisonOperator.IN:
      return buildIn(condition);
    case EComparisonOperator.LIKE:
      return buildLike(condition);
    case EComparisonOperator.GROUP:
      return buildGroup(condition);
  }
};

export const buildExtraConditions = (conditions?: IExtraCondition[]): string[] => {
  if (!conditions?.length) return [];
  return conditions.map(buildExtraCondition).filter(Boolean);
};
